import { ChevronLeft, ChevronRight } from '../../icons';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 번호 페이지네이션 (2026-08-21) — 워크스페이스 목록의 '더 보기' 를 대체한다.
 *
 * '더 보기' 는 누적이라 뒤로 갈수록 표가 길어지고, 어디쯤 보고 있는지·모두 몇 개인지
 * 알 방법이 없었다. 서버가 총 건수(`total`)를 주기 시작하면서 번호를 매길 수 있게 됐다.
 *
 * 재사용할 만한 기존 페이저가 프로젝트에 없어(AI 회의록의 이전/다음 인디케이터가 유일)
 * 새로 만든다. 폭이 좁아도 줄이 무너지지 않게 **가운데 창(window) 방식**으로 그린다 —
 * 페이지가 100장이어도 버튼 수는 일정하다.
 */

/** 쪽당 개수 후보. 첫 값이 기본. */
export const WORKSPACE_PAGE_SIZES = [50, 100] as const;

interface Props {
  page: number;
  pageSize: number;
  /** 총 **파일** 수 — 폴더는 구획이 따로라 세지 않는다. */
  total: number;
  disabled?: boolean;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}

/**
 * 그릴 번호들. `null` 은 생략 부호(…) 자리.
 *
 * 양 끝(1·마지막)은 늘 두고, 지금 쪽 좌우로 한 칸씩 편다. 앞뒤가 붙어 버리는 구간에서는
 * 생략 부호 대신 그 번호를 그대로 그린다 — `1 … 2 3` 처럼 "…" 가 한 장만 가리는 꼴을 피한다.
 */
export function pageWindow(page: number, pages: number): Array<number | null> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const out: Array<number | null> = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(pages - 1, page + 1);
  if (from > 2) out.push(from === 3 ? 2 : null);
  for (let n = from; n <= to; n += 1) out.push(n);
  if (to < pages - 1) out.push(to === pages - 2 ? pages - 1 : null);
  out.push(pages);
  return out;
}

export function WorkspacePagination({ page, pageSize, total, disabled, onPage, onPageSize }: Props) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  // 한 쪽에 다 들어가고 쪽당 개수를 바꿀 이유도 없으면 아예 그리지 않는다.
  if (total === 0 || (pages === 1 && total <= WORKSPACE_PAGE_SIZES[0])) return null;

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <nav className="workspace-pager" aria-label={tWorkspace('workspace.page.aria')}>
      <span className="workspace-pager-summary">
        {tWorkspace('workspace.page.summary', { total: String(total), from: String(from), to: String(to) })}
      </span>

      <div className="workspace-pager-nums">
        <button
          type="button"
          className="workspace-pager-btn"
          disabled={disabled || page <= 1}
          aria-label={tWorkspace('workspace.page.prev')}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft size={14} aria-hidden />
        </button>

        {pageWindow(page, pages).map((n, i) => (
          n === null
            // 생략 부호는 누를 것이 아니라 자리 표시다 — 버튼으로 만들면 탭 이동만 늘어난다.
            ? <span key={`gap-${i}`} className="workspace-pager-gap" aria-hidden>…</span>
            : (
              <button
                key={n}
                type="button"
                className={`workspace-pager-btn${n === page ? ' workspace-pager-btn--on' : ''}`}
                disabled={disabled}
                aria-current={n === page ? 'page' : undefined}
                aria-label={tWorkspace('workspace.page.goto', { n: String(n) })}
                onClick={() => onPage(n)}
              >
                {n}
              </button>
            )
        ))}

        <button
          type="button"
          className="workspace-pager-btn"
          disabled={disabled || page >= pages}
          aria-label={tWorkspace('workspace.page.next')}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight size={14} aria-hidden />
        </button>
      </div>

      <label className="workspace-pager-size">
        <span>{tWorkspace('workspace.page.size')}</span>
        <select
          value={pageSize}
          disabled={disabled}
          onChange={(e) => onPageSize(Number(e.target.value))}
        >
          {WORKSPACE_PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
    </nav>
  );
}
